/**
 * Pure helpers for the dubbing Timeline (L0 base).
 *
 * No React, no Redux — the scale/scroll math the whole timeline stands on.
 */

import {
  BLOCK_MARGIN,
  BLOCK_STATUS,
  CONVERTING_STATUS,
  DURATION_BLOCK_MARGIN,
} from '@src/constants/project';
import {
  APPEND_GAP_PX,
  BLOCK_VARIANT,
  CLIP_REVEAL_MARGIN_PX,
  CLIP_SKELETON_WIDTH_PX,
  EDGE_SCROLL_MAX_SPEED_PX,
  EDGE_SCROLL_SIZE_PX,
  MAX_ZOOM,
  MIN_ZOOM,
  ZOOM_STEP,
} from '@src/constants/timeline';
import { convertSubtitleTimeToSeconds } from '@src/utils/time';

// px-per-second at each end of the zoom range. MIN is chosen so a very long
// project (up to DUBBING_MAX_DURATION = 4h) does NOT fully fit a ~1000px
// viewport — Fit then clamps to MIN_ZOOM and the user scrolls.
const MIN_PX_PER_SEC = 0.1;
const MAX_PX_PER_SEC = 240;

// Exponential mapping so each zoom step feels like a constant ratio.
const ZOOM_RATIO =
  (MAX_PX_PER_SEC / MIN_PX_PER_SEC) ** (1 / (MAX_ZOOM - MIN_ZOOM));

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const pad2 = (n) => String(n).padStart(2, '0');

/** Clamp a (possibly fractional) zoom level into [MIN_ZOOM, MAX_ZOOM]. */
export const clampZoom = (zoom) => clamp(zoom, MIN_ZOOM, MAX_ZOOM);

/** Step the zoom in (more detail), clamped. */
export const zoomIn = (zoom) => clampZoom(zoom + ZOOM_STEP);

/** Step the zoom out (less detail), clamped. */
export const zoomOut = (zoom) => clampZoom(zoom - ZOOM_STEP);

/** Convert a zoom level into pixels-per-second (strictly increasing in zoom). */
export const zoomToPxPerSec = (zoom) =>
  MIN_PX_PER_SEC * ZOOM_RATIO ** (zoom - MIN_ZOOM);

/** Inverse of {@link zoomToPxPerSec}: recover the zoom level from px-per-second. */
export const pxPerSecToZoom = (pxPerSec) =>
  MIN_ZOOM + Math.log(pxPerSec / MIN_PX_PER_SEC) / Math.log(ZOOM_RATIO);

/** Position (px) of a point in time given the current scale. */
export const secToPx = (sec, pxPerSec) => sec * pxPerSec;

/** Time (sec) of a pixel offset given the current scale. Guards divide-by-zero. */
export const pxToSec = (px, pxPerSec) => (pxPerSec > 0 ? px / pxPerSec : 0);

/**
 * Mid-time (seconds) of filmstrip tile `index`.
 *
 * The strip is `stripWidth` px covering `duration` seconds, cut into tiles a
 * fixed `tileWidth` wide — so the last tile is usually partial. The time has to
 * come from the tile's own pixel span: dividing by the tile COUNT instead
 * (a ceil of `stripWidth / tileWidth`) compresses every tile's time toward zero,
 * measured up to 23.9s early on a 798s source at Fit zoom and worse the further
 * right the tile sits. A partial last tile is measured by the part of it that is
 * actually on the strip, so it never points past the clip. Pure.
 */
export const tileTimeAt = ({ index, tileWidth, stripWidth, duration }) => {
  if (!(tileWidth > 0) || !(stripWidth > 0) || !(duration > 0)) return 0;
  const from = Math.min(index * tileWidth, stripWidth);
  const to = Math.min(from + tileWidth, stripWidth);
  return ((from + to) / 2 / stripWidth) * duration;
};

/**
 * Zoom level (clamped) that fits the whole timeline inside `viewportWidth` with
 * no horizontal scroll: both the longest track (`durationSec`) and the append
 * buttons that overflow past their last clip ("Thêm khối" / "Thêm nhạc nền").
 *
 * `appendBoxes` are those buttons measured in content px at the CURRENT
 * `pxPerSec`: `{ leftPx, widthPx }`. Each button's right edge splits into a part
 * that scales with zoom (the track length up to it) and a fixed part (its width
 * + insets); keeping the fixed part out of the scale makes the fit idempotent —
 * re-fitting at the fitted zoom returns the same zoom.
 */
export const computeFitZoom = ({
  pxPerSec,
  viewportWidth,
  durationSec,
  appendBoxes = [],
  offsetPx = 0,
  gapPx = 0,
  endPadPx = 0,
}) => {
  if (!durationSec || durationSec <= 0 || viewportWidth <= 0) return MAX_ZOOM;
  // The longest track (plus the trailing pad after it) must fit; each append
  // button, sitting past its track, adds a tighter limit. Take the smallest
  // px-per-second any of them allows.
  let fitPxPerSec = (viewportWidth - offsetPx - endPadPx) / durationSec;
  appendBoxes.forEach(({ leftPx, widthPx }) => {
    const scalablePx = leftPx - offsetPx - gapPx; // track length up to the button
    if (scalablePx <= 0) return;
    const fixedPx = widthPx + offsetPx + gapPx;
    const limit = (pxPerSec * (viewportWidth - fixedPx)) / scalablePx;
    if (limit < fitPxPerSec) fitPxPerSec = limit;
  });
  if (fitPxPerSec <= 0) return MIN_ZOOM;
  return clampZoom(pxPerSecToZoom(fitPxPerSec));
};

const NICE_INTERVALS_SEC = [
  1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200,
];

/**
 * Smallest "nice" second-interval whose on-screen spacing (`interval * pxPerSec`)
 * is at least `minLabelGapPx`. The interval grows as the timeline zooms out.
 * The ruler builds its ticks from this (windowed to the visible range).
 */
export const pickTickInterval = (pxPerSec, minLabelGapPx = 60) =>
  NICE_INTERVALS_SEC.find((step) => step * pxPerSec >= minLabelGapPx) ||
  NICE_INTERVALS_SEC[NICE_INTERVALS_SEC.length - 1];

/**
 * Seconds window covered by the viewport (+ overscan), clamped to the project.
 * Used to window clips for performance.
 * @returns {{ fromSec: number, toSec: number }}
 */
export const computeVisibleRange = ({
  scrollLeft,
  viewportWidth,
  pxPerSec,
  durationSec,
  overscanPx = 200,
}) => {
  if (pxPerSec <= 0) return { fromSec: 0, toSec: durationSec };
  return {
    fromSec: clamp((scrollLeft - overscanPx) / pxPerSec, 0, durationSec),
    toSec: clamp(
      (scrollLeft + viewportWidth + overscanPx) / pxPerSec,
      0,
      durationSec,
    ),
  };
};

/** Format seconds as `m:ss`, or `h:mm:ss` once past an hour. Guards bad input. */
export const formatTimelineTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
};

const toSeconds = (time) => (time ? convertSubtitleTimeToSeconds(time) : 0);

// Status variant for a clip from its synthesis state (error vs default).
// Error when synthesis failed (`status`) or the reducer flagged it
// (`blockStatus`) — `status` is checked too because `blockStatus` can lag / be
// absent on freshly-loaded blocks.
//
// Text-validation errors are NOT decided here: they are layered on per visible
// clip in the container (see VoiceoverTrack), so we don't run the editor's
// `checkBlockTextError` (regex + blacklist scan) for every block on each
// change. This keeps blocksToClips cheap — the whole project is mapped, only
// the on-screen clips pay for text validation.
const clipVariant = (block) =>
  block.status === CONVERTING_STATUS.FAILURE ||
  block.blockStatus === BLOCK_STATUS.ERROR
    ? BLOCK_VARIANT.ERROR
    : BLOCK_VARIANT.DEFAULT;

/**
 * Status variant for a clip (drives its fill/icon, NOT its border): an error
 * block always shows as error; otherwise a block whose audio is trimmed shows
 * as trimmed, and the rest as default. Selection is applied separately at
 * render (the `selected` prop), so selecting a block never rebuilds the clip
 * list — important for large projects.
 */
export const resolveClipVariant = (baseVariant, isTrimmed) => {
  if (baseVariant === BLOCK_VARIANT.ERROR) return BLOCK_VARIANT.ERROR;
  return isTrimmed ? BLOCK_VARIANT.TRIMMED : BLOCK_VARIANT.DEFAULT;
};

/**
 * Map dubbing blocks (redux `multiBlock`) into timeline clip view-models.
 * Pure & cheap — SRT timing (`HH:mm:ss,SSS`) → seconds plus a status-only
 * variant, so mapping the whole project stays inexpensive on every change.
 * Preserves `blockIds` order; skips missing blocks. Text-validation errors are
 * applied later, per visible clip (see VoiceoverTrack).
 *
 * When `useTranslatedText` is set (a successful translation is active) the clip
 * shows `translatedText`, matching what the block editor / synthesis use — so
 * the timeline label stays in sync with the text that will actually be spoken.
 *
 * @param {Record<string, object>} blocks
 * @param {string[]} blockIds
 * @param {{ useTranslatedText?: boolean }} [options]
 * @returns {{ id, startSec, endSec, text, variant }[]}
 */
export const blocksToClips = (blocks = {}, blockIds = [], options = {}) => {
  const { useTranslatedText = false } = options;
  return blockIds
    .map((id) => blocks[id])
    .filter(Boolean)
    .map((block) => ({
      id: block.id,
      startSec: toSeconds(block.startTime),
      endSec: toSeconds(block.endTime),
      text: (useTranslatedText ? block.translatedText : block.text) || '',
      variant: clipVariant(block),
    }));
};

/**
 * Latest block end time (seconds) — the timeline's content extent. Cheap: no
 * clip objects are built, so sizing the timeline stays O(n) without extra
 * allocations even for thousands of blocks.
 */
export const getBlocksMaxEndSec = (blocks = {}, blockIds = []) =>
  blockIds.reduce((max, id) => {
    const end = toSeconds(blocks[id]?.endTime);
    return end > max ? end : max;
  }, 0);

/**
 * Whether an end block ("Thêm block cuối") may be appended to the voiceover
 * track. Allowed only while the project still has room before the
 * duration limit: `total < maxDuration - BLOCK_MARGIN`. The boundary is
 * exclusive, so a project sitting exactly at (or past) the limit can't append.
 */
export const canAppendEndBlock = (totalDurationSec, maxDurationSec) =>
  totalDurationSec < maxDurationSec - BLOCK_MARGIN;

/**
 * Duration (seconds) of an appended end block
 * `MIN(2s, maxDuration - total + BLOCK_MARGIN)` — the standard 2s block, or
 * whatever time is left before the duration limit if less. Without a limit
 * (default) it's always the full 2s.
 */
export const getEndBlockDurationSec = (
  totalDurationSec,
  maxDurationSec = Infinity,
) => {
  const duration = Math.min(
    DURATION_BLOCK_MARGIN,
    maxDurationSec - totalDurationSec + BLOCK_MARGIN,
  );
  // Round to millisecond precision (SRT granularity) so floating-point noise
  // like 0.9930000000001892 never leaks into the block timing.
  return Number(duration.toFixed(3));
};

/**
 * Auto-scroll velocity (px per animation frame, signed) for a drag-seek whose
 * pointer has reached a viewport edge. Zero in the dead zone; ramps linearly
 * from 0 to `maxSpeed` across the `edgeSize`-px band at each edge (and clamps
 * there if the pointer is dragged past the edge). Negative scrolls left, positive
 * scrolls right — so holding the pointer at an edge keeps revealing content.
 *
 * @param {number} pointerX      pointer client X
 * @param {number} viewportLeft  client X of the scrollable area's left edge
 * @param {number} viewportWidth width (px) of the scrollable area
 */
export const computeEdgeScrollVelocity = (
  pointerX,
  viewportLeft,
  viewportWidth,
  { edgeSize = EDGE_SCROLL_SIZE_PX, maxSpeed = EDGE_SCROLL_MAX_SPEED_PX } = {},
) => {
  if (viewportWidth <= 0 || edgeSize <= 0) return 0;
  const viewportRight = viewportLeft + viewportWidth;
  if (pointerX < viewportLeft + edgeSize) {
    const depth = clamp((viewportLeft + edgeSize - pointerX) / edgeSize, 0, 1);
    return -depth * maxSpeed;
  }
  if (pointerX > viewportRight - edgeSize) {
    const depth = clamp(
      (pointerX - (viewportRight - edgeSize)) / edgeSize,
      0,
      1,
    );
    return depth * maxSpeed;
  }
  return 0;
};

// Lower/upper time bound for the clip at `index` of an ascending,
// non-overlapping list: the previous clip's end (or 0) and the next clip's
// start (or `totalSec`).
export const neighborBounds = (clips, index, totalSec) => ({
  minStartSec: index > 0 ? clips[index - 1].endSec : 0,
  maxEndSec: index < clips.length - 1 ? clips[index + 1].startSec : totalSec,
});

// New end edge from a raw dragged value: never past the next neighbour
// (maxEndSec), never longer than the audio (`maxDurationSec` — the timeline rule
// that a footage's duration can't exceed its object's total duration), and never
// within `minDurationSec` of the fixed start. `maxDurationSec` defaults to no cap
// (Infinity) so an unmeasured audio still trims by neighbour/min bounds.
export const clampTrimEnd = ({
  startSec,
  rawEndSec,
  maxEndSec,
  minDurationSec,
  maxDurationSec = Infinity,
}) => ({
  startSec,
  endSec: Math.max(
    Math.min(rawEndSec, maxEndSec, startSec + maxDurationSec),
    startSec + minDurationSec,
  ),
});

// New start edge from a raw dragged value: never before the previous neighbour
// (minStartSec), never so far left that the slot exceeds the audio length
// (`maxDurationSec`), and never within `minDurationSec` of the fixed end.
export const clampTrimStart = ({
  endSec,
  rawStartSec,
  minStartSec,
  minDurationSec,
  maxDurationSec = Infinity,
}) => ({
  startSec: Math.min(
    Math.max(rawStartSec, minStartSec, endSec - maxDurationSec),
    endSec - minDurationSec,
  ),
  endSec,
});

// Shift a clip by `deltaSec`, preserving its duration, clamped so it stays
// within [minStartSec, maxEndSec] (never crossing a neighbour or the project
// bounds). Moving doesn't change duration, so no audio-length cap is needed.
export const clampMove = ({
  startSec,
  endSec,
  deltaSec,
  minStartSec,
  maxEndSec,
}) => {
  const duration = endSec - startSec;
  const nextStart = Math.min(
    Math.max(startSec + deltaSec, minStartSec),
    maxEndSec - duration,
  );
  return { startSec: nextStart, endSec: nextStart + duration };
};

/**
 * Tail layout of the voice lane: where the "block still coming" skeleton goes
 * and where the append ("Thêm khối") button ends up.
 *
 * Both sit past the last clip's end, one APPEND_GAP_PX apart. With the skeleton
 * showing it takes the append button's usual slot and pushes the button out by
 * its own width plus another gap. `lastEndPx` is 0 when no clip exists yet, so
 * the skeleton lands at the lane start — the extraction-loading design. Pure.
 */
export const voiceoverLaneLayout = ({
  lastEndPx = 0,
  showSkeleton = false,
}) => {
  const tailLeft = lastEndPx + APPEND_GAP_PX;
  if (!showSkeleton) return { skeletonLeft: null, appendLeft: tailLeft };
  return {
    skeletonLeft: tailLeft,
    appendLeft: tailLeft + CLIP_SKELETON_WIDTH_PX + APPEND_GAP_PX,
  };
};

/**
 * Horizontal scroll delta (px) that brings a clip into view, or 0 when it is
 * already fully visible with `marginPx` to spare on both sides. Positive scrolls
 * right, negative left; the caller clamps to the scrollable range.
 *
 * A clip past the right edge is pulled just inside it, so scrolling stays as
 * small as possible. A clip past the LEFT edge — or one too wide to fit the
 * usable window at all — is aligned by its start instead, because its start is
 * the part worth looking at.
 *
 * A non-positive `viewportWidth` means the viewport has not been measured yet
 * (nothing is "in view", so every clip would look off-screen) — stay put and let
 * the caller ask again once layout has happened. Pure.
 */
export const clipRevealDelta = ({
  clipLeftPx,
  clipRightPx,
  scrollLeft,
  viewportWidth,
  marginPx = CLIP_REVEAL_MARGIN_PX,
}) => {
  if (viewportWidth <= 0) return 0;
  const viewFrom = scrollLeft + marginPx;
  const viewTo = scrollLeft + viewportWidth - marginPx;
  if (clipLeftPx >= viewFrom && clipRightPx <= viewTo) return 0;
  const tooWide = clipRightPx - clipLeftPx > viewTo - viewFrom;
  if (clipLeftPx < viewFrom || tooWide) return clipLeftPx - viewFrom;
  return clipRightPx - viewTo;
};

// Elements that own their own pointer gesture: a clip (voice or music) selects
// / moves / trims, an append button adds. A press starting on one must NOT also
// scrub the playhead, so they opt out of the timeline's seek surface by marker
// attribute rather than by each lane re-implementing the exclusion.
const NO_SEEK_SELECTOR = '[data-timeline-clip], [data-timeline-append]';

// Should a pointer-down on `target` seek? True for everything on the timeline
// surface — a lane's empty space, the gaps between lanes, the media clip, a
// skeleton — except inside the gesture-owning elements above. Pure: reads only
// the target's ancestor chain. Defaults to seekable for a non-Element target
// (e.g. `document`), which can only be the surface itself.
export const isSeekSurface = (target) =>
  typeof target?.closest !== 'function' || !target.closest(NO_SEEK_SELECTOR);

// Snap `sec` to the nearest candidate within `thresholdSec`, else return `sec`
// unchanged. Lets a dragged edge click onto a neighbour's boundary instead of
// landing a pixel short. Pure.
export const snapSec = (sec, candidates, thresholdSec) => {
  let best = sec;
  let bestDist = thresholdSec;
  candidates.forEach((candidate) => {
    const dist = Math.abs(candidate - sec);
    if (dist <= bestDist) {
      bestDist = dist;
      best = candidate;
    }
  });
  return best;
};
